/**
 * layers.ts — Camadas e posicionamento governado do Studio (Fase 3)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno): é a única fonte de verdade das
 * regras de camada, usada pelo editor, pelo renderizador web e pelo compilador
 * HTML das Edge Functions.
 *
 * Conceito para o usuário comum: em vez de "z-index" e "position", ele escolhe
 * ONDE o elemento fica (no fluxo, ancorado na seção ou grudado ao rolar), em
 * QUAL profundidade (atrás, base, conteúdo, sobreposição, flutuante, topo) e um
 * deslocamento fino — tudo podendo variar por tamanho de tela.
 *
 * Guarda-corpos obrigatórios:
 *  - `fixed` NÃO é opção comum (fica reservado como capacidade especial);
 *  - posicionamento ancorado só vale dentro de uma seção com "camadas livres"
 *    ligadas (o CSS só ativa `absolute` dentro de `.st-layers`);
 *  - no celular tudo volta ao fluxo normal, salvo escolha explícita.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (motor de composições, Fase 3)
 */

// Sem imports de propósito: este módulo também é lido pelo Deno (Edge
// Functions). Os tipos abaixo espelham `StudioBreakpoint`/`StudioResponsiveValue`.

/** Tamanhos de tela atendidos pelo Studio. */
type StudioBreakpoint = 'desktop' | 'tablet' | 'mobile';

/** Valor que pode variar por tamanho de tela. */
type StudioResponsiveValue<T> = T | { desktop?: T; tablet?: T; mobile?: T };

/** Resolve o valor da tela, herdando do tamanho maior para o menor. */
function resolver<T>(
  valor: StudioResponsiveValue<T> | undefined,
  tela: StudioBreakpoint,
): T | undefined {
  if (valor === undefined || valor === null) return undefined;
  if (typeof valor !== 'object' || Array.isArray(valor)) return valor as T;
  const mapa = valor as { desktop?: T; tablet?: T; mobile?: T };
  const cascata: StudioBreakpoint[] =
    tela === 'mobile' ? ['mobile', 'tablet', 'desktop']
    : tela === 'tablet' ? ['tablet', 'desktop']
    : ['desktop'];
  for (const chave of cascata) {
    const encontrado = mapa[chave];
    if (encontrado !== undefined && encontrado !== null && encontrado !== '') return encontrado;
  }
  return undefined;
}

/** Modos de posição permitidos no catálogo comum. */
export const STUDIO_POSITION_OPTIONS = [
  { value: 'flow', label: 'No fluxo (padrão)' },
  { value: 'relative', label: 'Deslocado do lugar' },
  { value: 'absolute', label: 'Ancorado na seção' },
  { value: 'sticky', label: 'Gruda ao rolar' },
];

/**
 * `fixed` fica FORA das opções comuns: é capacidade especial, liberada caso a
 * caso. O renderizador aceita o valor, mas ele nunca aparece no painel.
 */
export const STUDIO_POSITION_SPECIAL = 'fixed';

/** Profundidades nomeadas (nunca número livre). */
export const STUDIO_DEPTH_OPTIONS = [
  { value: 'behind', label: 'Atrás do fundo' },
  { value: 'base', label: 'Base' },
  { value: 'content', label: 'Conteúdo (padrão)' },
  { value: 'overlay', label: 'Sobreposição' },
  { value: 'floating', label: 'Flutuante' },
  { value: 'top', label: 'Topo' },
];

/** Âncoras disponíveis quando a posição é "ancorado na seção". */
export const STUDIO_ANCHOR_OPTIONS = [
  { value: 'top-left', label: 'Canto superior esquerdo' },
  { value: 'top-center', label: 'Topo ao centro' },
  { value: 'top-right', label: 'Canto superior direito' },
  { value: 'middle-left', label: 'Meio à esquerda' },
  { value: 'center', label: 'Centro' },
  { value: 'middle-right', label: 'Meio à direita' },
  { value: 'bottom-left', label: 'Canto inferior esquerdo' },
  { value: 'bottom-center', label: 'Rodapé ao centro' },
  { value: 'bottom-right', label: 'Canto inferior direito' },
];

const POSICOES_VALIDAS = new Set([
  'flow',
  'relative',
  'absolute',
  'sticky',
  STUDIO_POSITION_SPECIAL,
]);
const PROFUNDIDADES_VALIDAS = new Set(STUDIO_DEPTH_OPTIONS.map((o) => o.value));
const ANCORAS_VALIDAS = new Set(STUDIO_ANCHOR_OPTIONS.map((o) => o.value));

/** Conteúdo do grupo de propriedades `layer` gravado no documento. */
export interface StudioLayerProps {
  position?: StudioResponsiveValue<string>;
  depth?: string;
  anchor?: string;
  offsetX?: StudioResponsiveValue<string>;
  offsetY?: StudioResponsiveValue<string>;
  rotate?: StudioResponsiveValue<string | number>;
  scale?: StudioResponsiveValue<string | number>;
  opacity?: StudioResponsiveValue<string | number>;
  /** Mantém o posicionamento também no celular (por padrão volta ao fluxo). */
  keepOnMobile?: boolean;
  /** Distância do topo quando a posição é "gruda ao rolar". */
  stickyTop?: string;
}

/** Lê o grupo `layer` de um bloco, aceitando ausência total. */
export function lerCamada(props: unknown): StudioLayerProps | null {
  if (!props || typeof props !== 'object') return null;
  const camada = (props as Record<string, unknown>).layer;
  if (!camada || typeof camada !== 'object' || Array.isArray(camada)) return null;
  return camada as StudioLayerProps;
}

/**
 * Verdadeiro quando o bloco realmente pede uma camada.
 * Documento antigo (sem `layer`) nunca entra neste caminho — nada muda nele.
 */
export function temCamada(props: unknown): boolean {
  const camada = lerCamada(props);
  if (!camada) return false;
  const posicao = resolver<string>(camada.position, 'desktop');
  if (posicao && posicao !== 'flow' && POSICOES_VALIDAS.has(posicao)) return true;
  return Boolean(
    camada.depth ||
      camada.offsetX ||
      camada.offsetY ||
      camada.rotate ||
      camada.scale ||
      camada.opacity !== undefined,
  );
}

/** Classes CSS da camada (posição, profundidade, âncora e exceções). */
export function classesDaCamada(props: unknown): string {
  const camada = lerCamada(props);
  if (!camada) return '';
  const classes = ['st-layer'];

  const posicao = resolver<string>(camada.position, 'desktop');
  if (posicao && posicao !== 'flow' && POSICOES_VALIDAS.has(posicao)) {
    classes.push(`st-pos-${posicao}`);
    const ancora = String(camada.anchor ?? 'top-left');
    if (posicao === 'absolute' || posicao === STUDIO_POSITION_SPECIAL) {
      classes.push(`st-an-${ANCORAS_VALIDAS.has(ancora) ? ancora : 'top-left'}`);
    }
  }

  const profundidade = String(camada.depth ?? '');
  if (profundidade && PROFUNDIDADES_VALIDAS.has(profundidade)) {
    classes.push(`st-z-${profundidade}`);
  }

  if (camada.keepOnMobile) classes.push('st-keep-mobile');
  return classes.join(' ');
}

/** Adiciona a variável apenas quando há valor para aquele tamanho de tela. */
function variavel(
  destino: Record<string, string>,
  nome: string,
  valor: StudioResponsiveValue<string | number> | undefined,
  sufixo: (bruto: string) => string,
): void {
  (['desktop', 'tablet', 'mobile'] as StudioBreakpoint[]).forEach((tela) => {
    const encontrado = resolver<string | number>(valor, tela);
    if (encontrado === undefined) return;
    // Só grava quando o próprio nível declarou algo (a herança já é feita no CSS)
    const declaradoAqui =
      typeof valor !== 'object' || Array.isArray(valor)
        ? tela === 'desktop'
        : (valor as Record<string, unknown>)[tela] !== undefined;
    if (!declaradoAqui) return;
    const chave = tela === 'desktop' ? `${nome}-d` : tela === 'tablet' ? `${nome}-t` : `${nome}-m`;
    destino[chave] = sufixo(String(encontrado));
  });
}

/** Acrescenta unidade quando o usuário digitou apenas o número. */
function comUnidade(bruto: string, unidade: string): string {
  const texto = bruto.trim();
  if (!texto) return `0${unidade}`;
  return /^-?\d+(\.\d+)?$/.test(texto) ? `${texto}${unidade}` : texto;
}

/**
 * Variáveis CSS da camada (deslocamento, rotação, escala, opacidade).
 * O CSS combina tudo em um único `transform`, respeitando a herança
 * computador → tablet → celular.
 */
export function variaveisDaCamada(props: unknown): Record<string, string> {
  const camada = lerCamada(props);
  if (!camada) return {};
  const vars: Record<string, string> = {};
  variavel(vars, '--st-x', camada.offsetX, (v) => comUnidade(v, 'px'));
  variavel(vars, '--st-y', camada.offsetY, (v) => comUnidade(v, 'px'));
  variavel(vars, '--st-rot', camada.rotate, (v) => comUnidade(v, 'deg'));
  variavel(vars, '--st-scale', camada.scale, (v) => v.trim() || '1');
  variavel(vars, '--st-op', camada.opacity, (v) => v.trim() || '1');
  if (camada.stickyTop) vars['--st-sticky'] = comUnidade(String(camada.stickyTop), 'px');
  return vars;
}

/** Mesmas variáveis, já em texto para o atributo `style` do HTML compilado. */
export function estiloDaCamada(props: unknown): string {
  const vars = variaveisDaCamada(props);
  return Object.entries(vars)
    // Valores são numéricos/dimensões; ainda assim tudo que não casar é descartado.
    .filter(([, valor]) => /^[-\w.%,() ]+$/.test(valor))
    .map(([chave, valor]) => `${chave}:${valor}`)
    .join(';');
}
